import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const MOBILE_CHECKOUT_MQ = '(max-width: 749px)';
/** iPhone SE and similar — Shopify stacks wallet buttons vertically */
const NARROW_MOBILE_CHECKOUT_MQ = '(max-width: 400px)';

const WALLET_STYLE_ID = 'cart-drawer-wallet-mobile';

const WALLET_STYLES_HORIZONTAL = `
  .wallet-cart-grid {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    justify-content: center !important;
    align-items: stretch !important;
    gap: 6px !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  .accelerated-checkout-button-container,
  .wallet-cart-button-container {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    max-width: 33.333% !important;
    width: auto !important;
  }
  .accelerated-checkout-button-container > *,
  .wallet-cart-button-container > * {
    width: 100% !important;
    min-height: 40px !important;
    height: 40px !important;
    max-height: 40px !important;
  }
`;

const WALLET_STYLES_VERTICAL_COMPACT = `
  .wallet-cart-grid {
    display: flex !important;
    flex-direction: column !important;
    flex-wrap: nowrap !important;
    align-items: stretch !important;
    gap: 4px !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  .accelerated-checkout-button-container,
  .wallet-cart-button-container {
    flex: 0 0 auto !important;
    min-width: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
  }
  .accelerated-checkout-button-container > *,
  .wallet-cart-button-container > * {
    width: 100% !important;
    min-height: 32px !important;
    height: 32px !important;
    max-height: 32px !important;
  }
`;

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
export class CartDrawerComponent extends DialogComponent {
  /** @type {MediaQueryList | undefined} */
  #mobileCheckoutMq;

  /** @type {MediaQueryList | undefined} */
  #narrowMobileCheckoutMq;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #walletLayoutTimer;

  /** @type {MutationObserver | undefined} */
  #consentObserver;

  /** @type {(() => void) | null} Cleanup fn for the injected consent icon + popup */
  #consentCleanup = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:update', this.#scheduleMobileWalletLayout);
    document.addEventListener('cart:update', this.#shortenBuyerConsent);
    this.#mobileCheckoutMq = window.matchMedia(MOBILE_CHECKOUT_MQ);
    this.#narrowMobileCheckoutMq = window.matchMedia(NARROW_MOBILE_CHECKOUT_MQ);
    this.#mobileCheckoutMq.addEventListener('change', this.#scheduleMobileWalletLayout);
    this.#narrowMobileCheckoutMq.addEventListener('change', this.#scheduleMobileWalletLayout);
    this.#scheduleMobileWalletLayout();

    // Watch for Shopify's buyer consent element being injected and shorten its text
    this.#consentObserver = new MutationObserver(() => this.#shortenBuyerConsent());
    this.#consentObserver.observe(this, { childList: true, subtree: true });
    this.#shortenBuyerConsent();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:update', this.#scheduleMobileWalletLayout);
    document.removeEventListener('cart:update', this.#shortenBuyerConsent);
    this.#mobileCheckoutMq?.removeEventListener('change', this.#scheduleMobileWalletLayout);
    this.#narrowMobileCheckoutMq?.removeEventListener('change', this.#scheduleMobileWalletLayout);
    this.#consentObserver?.disconnect();
    if (this.#consentCleanup) this.#consentCleanup();
    clearTimeout(this.#walletLayoutTimer);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Shopify injects a verbose subscription consent <small> element.
   * We keep it hidden (via CSS) for compliance and instead inject a compact
   * info-icon button that opens a centered popup with the full policy text.
   */
  #shortenBuyerConsent = () => {
    const el = /** @type {HTMLElement | null} */ (this.querySelector('#shopify-buyer-consent'));
    if (!el || el.dataset.shortened) return;
    const policyBtn = /** @type {HTMLAnchorElement | null} */ (
      el.querySelector('#shopify-subscription-policy-button')
    );
    if (!policyBtn) return;

    // Mark as processed so this guard fires only once per injected element
    el.dataset.shortened = '1';

    // Tear down any previously injected icon/popup (e.g. after a cart morph)
    if (this.#consentCleanup) this.#consentCleanup();

    const policyHref = policyBtn.getAttribute('href') || '#';
    const policyText = policyBtn.textContent.trim() || 'cancellation policy';

    // ── Info icon button ───────────────────────────────────────────────────
    const iconBtn = document.createElement('button');
    iconBtn.type = 'button';
    iconBtn.className = 'cd-consent-icon-btn';
    iconBtn.setAttribute('aria-haspopup', 'dialog');
    iconBtn.innerHTML =
      '<span class="cd-consent-icon-btn__icon" aria-hidden="true">i</span>\u00a0Subscription info';

    // ── Popup — use native <dialog> so it enters the browser top layer,
    //    rendering above the cart drawer's own <dialog> element ─────────────
    const popup = /** @type {HTMLDialogElement} */ (document.createElement('dialog'));
    popup.className = 'cd-consent-popup';
    popup.setAttribute('aria-labelledby', 'cd-consent-popup-title');
    popup.innerHTML = `<div class="cd-consent-popup__inner">
  <button class="cd-consent-popup__close" type="button" aria-label="Close">&#x2715;</button>
  <p class="cd-consent-popup__title" id="cd-consent-popup-title">Subscription Policy</p>
  <p class="cd-consent-popup__text">This order contains a subscription or recurring purchase.
    By continuing, you agree to the
    <a class="cd-consent-popup__link" href="${policyHref}">${policyText}</a>
    and authorize charges at the prices and frequency listed until your order is fulfilled or you cancel.
  </p>
</div>`;
    document.body.appendChild(popup);

    const closeBtnEl = /** @type {HTMLElement | null} */ (
      popup.querySelector('.cd-consent-popup__close')
    );

    const closePopup = () => popup.close();
    const openPopup = () => {
      popup.showModal();
      closeBtnEl?.focus();
    };

    iconBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openPopup();
    });
    // Click on the backdrop area (the <dialog> element itself, outside the inner card)
    popup.addEventListener('click', (e) => {
      if (e.target === popup) closePopup();
    });
    closeBtnEl?.addEventListener('click', closePopup);
    // Escape is handled natively by showModal() — no extra keydown listener needed

    // Insert icon right after the hidden Shopify element
    el.after(iconBtn);

    // Store cleanup so we can remove on disconnect or re-process
    this.#consentCleanup = () => {
      iconBtn.remove();
      popup.remove();
      this.#consentCleanup = null;
    };
  };

  #scheduleMobileWalletLayout = () => {
    clearTimeout(this.#walletLayoutTimer);
    const run = () => this.#layoutMobileWalletButtons();
    run();
    requestAnimationFrame(run);
    this.#walletLayoutTimer = setTimeout(run, 150);
    setTimeout(run, 600);
  };

  /**
   * Mobile drawer: horizontal wallet row on typical phones; compact vertical stack on very narrow screens.
   */
  #layoutMobileWalletButtons() {
    const mobile = this.#mobileCheckoutMq?.matches ?? window.matchMedia(MOBILE_CHECKOUT_MQ).matches;
    const narrow =
      this.#narrowMobileCheckoutMq?.matches ?? window.matchMedia(NARROW_MOBILE_CHECKOUT_MQ).matches;

    this.toggleAttribute('data-wallet-stack', mobile && narrow);

    this.querySelectorAll('shopify-accelerated-checkout-cart').forEach((host) => {
      const root = host.shadowRoot;
      if (!root) return;

      let style = root.getElementById(WALLET_STYLE_ID);

      if (!mobile) {
        style?.remove();
        return;
      }

      if (!style) {
        style = document.createElement('style');
        style.id = WALLET_STYLE_ID;
        root.appendChild(style);
      }

      style.textContent = narrow ? WALLET_STYLES_VERTICAL_COMPACT : WALLET_STYLES_HORIZONTAL;
    });
  }

  open() {
    this.showDialog();
    document.dispatchEvent(new Event('cart-drawer:open'));
    this.#scheduleMobileWalletLayout();
    this.#shortenBuyerConsent();
    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
